
import { motion } from "framer-motion";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { useQuery } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { companies } from '../../../db';
import { createCompanySlug } from "@/lib/companyUtils";
import { getCompanyDisplayInfo, isTempEmployment } from "@/lib/companyDisplayUtils";
import { getActivitySkillsCount, createActivitySlug } from "@/lib/activityUtils";
import DualCompanyLogo from "@/components/ui/dual-company-logo";
import DualCompanyName from "@/components/ui/dual-company-name";

type ActivityEvent = {
  id: number;
  title: string;
  type: 'work' | 'education';
  companyId: number;
  tempAgencyId?: number;
  startDate: string;
  endDate: string | null;
  description: string;
};

async function fetchActivities(companyId?: number): Promise<ActivityEvent[]> {
  const url = companyId ? `/api/activities/company/${companyId}` : '/api/activities';
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error('Failed to fetch activities');
  }
  return response.json();
}

interface ActivitiesProps {
  companyId?: number;
  showHeader?: boolean;
}

export default function Activities({ companyId, showHeader = true }: ActivitiesProps) {
  const [, setLocation] = useLocation();
  const { data: activityEvents = [], isLoading } = useQuery({
    queryKey: ['activities', companyId],
    queryFn: () => fetchActivities(companyId)
  });

  if (isLoading) {
    return <div>Loading...</div>;
  }

  // Sort activities by start date in descending order
  const sortedActivities = [...activityEvents].sort(
    (a, b) => new Date(b.startDate).getTime() - new Date(a.startDate).getTime()
  );

  const timelineContent = (
    <div className="relative pl-8 space-y-8">
      {sortedActivities.map((event, index) => {
        const displayInfo = getCompanyDisplayInfo(event, companies);
        const skillsCount = getActivitySkillsCount(event.id);
        
        return (
          <motion.div
            key={event.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.2 }}
            className="relative"
          >
            <div className="absolute left-[-2rem] w-4 h-4 rounded-full bg-primary" />
            {index !== sortedActivities.length - 1 && (
              <div className="absolute left-[-1.5rem] top-4 w-0.5 h-full bg-primary/20" />
            )}
            <div className="mb-1 cursor-pointer" onClick={() => setLocation(`/activities/${createActivitySlug(event.title)}`)}>
              <div className="flex items-center gap-2 mb-2">
                <span className="text-sm text-primary font-medium">
                  {new Date(event.startDate).getFullYear()}
                  {event.endDate ? ` - ${new Date(event.endDate).getFullYear()}` : ' - Present'}
                </span>
                <span className={`text-xs px-2 py-0.2 rounded-full ${event.type === 'work' ? 'bg-blue-100 text-blue-800' : 'bg-green-100 text-green-800'}`}>
                  {event.type}
                </span>
                {skillsCount > 0 && (
                  <Badge variant="outline" className="text-xs">
                    {skillsCount} skills
                  </Badge>
                )}
              </div>
              <h3 className="text-lg font-semibold">{event.title}</h3>
              <div className="flex items-center gap-2 mb-2">
                {displayInfo.isDualCompany ? (
                  <DualCompanyLogo
                    assignmentCompany={displayInfo.assignmentCompany}
                    tempAgency={displayInfo.tempAgency!}
                    size="sm"
                    onAssignmentCompanyClick={(e) => {
                      e.stopPropagation();
                      setLocation(`/professional/${createCompanySlug(displayInfo.assignmentCompany.name)}`);
                    }}
                    onTempAgencyClick={(e) => {
                      e.stopPropagation();
                      setLocation(`/professional/${createCompanySlug(displayInfo.tempAgency!.name)}`);
                    }}
                  />
                ) : (
                  <Avatar 
                    className="h-6 w-6 cursor-pointer hover:scale-110 transition-transform" 
                    onClick={(e) => {
                      e.stopPropagation();
                      setLocation(`/professional/${createCompanySlug(displayInfo.assignmentCompany.name)}`);
                    }}
                  >
                    <AvatarImage
                      src={displayInfo.assignmentCompany.logoPath || undefined}
                      alt="Company logo"
                      className="object-cover"
                    />
                    <AvatarFallback>{displayInfo.assignmentCompany.logoText}</AvatarFallback>
                  </Avatar>
                )}
                {displayInfo.isDualCompany ? (
                  <DualCompanyName
                    assignmentCompany={displayInfo.assignmentCompany}
                    tempAgency={displayInfo.tempAgency!}
                    className="text-sm text-muted-foreground"
                  />
                ) : (
                  <span className="text-sm text-muted-foreground">
                    {displayInfo.assignmentCompany.name}
                  </span>
                )}
              </div>
              <p className="text-sm text-muted-foreground">{event.description}</p>
            </div>
          </motion.div>
        );
      })}
    </div>
  );  if (!showHeader) {
    return timelineContent;
  }

  return (
    <Card className="w-full">
      <CardHeader>
        <CardTitle>Activities</CardTitle>
      </CardHeader>
      <CardContent>
        {timelineContent}
      </CardContent>
    </Card>
  );
}
